import { SVGAssets } from '@business/assets/vectors';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { routePaths } from '@utils/constants/routePaths';
import { setCommonState } from '@ports/common/commonStateSlice';
import { UploadPhotoModal } from '@business/workflows/ComponentUI/MyProfileForm/UploadPhotoModal';
import { Button } from '../Button';

interface EditProfilePictureModalProps {
  isOpen: boolean;
  userId: string;
  onClose: () => void;
  onComplete?: () => void;
  onUploadSuccess?: () => void;
}

export const EditProfilePictureModal: FC<EditProfilePictureModalProps> = ({
  isOpen,
  userId,
  onClose,
  onComplete,
  onUploadSuccess
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);

  if (!isOpen) return null;

  const handleTakePicture = () => {
    dispatch(setCommonState({ isFromPhoto: false }));
    navigate(routePaths.Camera, { state: { returnTo: 'photos' } });
  };

  const handleUploadSuccess = async () => {
    if (onUploadSuccess) {
      await onUploadSuccess();
    }
    setShowUploadModal(false);
    if (onComplete) {
      onComplete();
    } else {
      onClose();
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="relative w-full max-w-md confirmation-box-style bg-white shadow-lg">
          <div className="flex justify-between items-center px-5 py-4">
            <h3 className="text-lg font-medium text-[#505050]">
              {t('editProfilePicture.title')}
            </h3>
            <Button
              btnStyle="link"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600">
              <SVGAssets.CloseIconGray className="w-5 h-4 text-gray-400" />
            </Button>
          </div>
          <hr className="border-t border-gray-200 w-full" />

          <div className="flex justify-center items-center gap-4 px-5 py-8">
            <div
              className="flex flex-col items-center justify-center gap-2 w-32 h-24 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setShowUploadModal(true)}>
              <SVGAssets.UploadIcon className="w-6 h-6 text-[#353535]" />
              <span className="text-sm font-medium text-[#353535]">
                {t('editProfilePicture.upload')}
              </span>
            </div>
            <div
              className="flex flex-col items-center justify-center gap-2 w-32 h-24 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={handleTakePicture}>
              <SVGAssets.TakePicIcon className="w-6 h-6 text-[#353535]" />
              <span className="text-sm font-medium text-[#353535]">
                {t('editProfilePicture.selectPhoto')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {showUploadModal && (
        <UploadPhotoModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          userId={userId}
          onUploadSuccess={handleUploadSuccess}
          isPeopleProfilePic={false}
          skipNavigateOnSuccess
          hideGallerySection
        />
      )}
    </>
  );
};
